/**
 * Audio Upload Core - Shared upload utilities for audio messages
 * Mirrors photo-upload-core.js pattern for consistency
 */

import { getApiToken } from './tokenUtils.js';
import { t } from './i18n.js';
import { apiErrorMessage } from './api-error-message.js';
import { sendXhr } from './xhr-send.js';

export const AUDIO_UPLOAD_ENDPOINT = '/v1/audio';

export const AudioUploadConfig = {
    MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
    MAX_DURATION: 60, // seconds
    MIN_DURATION: 1,  // seconds
    MIME_TYPE: 'audio/webm'
};

export const AudioUploadErrors = {
    get AUTH_REQUIRED() { return t('common.audio_auth_required'); },
    get UPLOAD_FAILED() { return t('common.audio_upload_failed'); },
    get NETWORK_ERROR() { return t('common.audio_network_error'); },
    get UPLOAD_CANCELLED() { return t('common.audio_upload_cancelled'); },
    get INVALID_RESPONSE() { return t('common.audio_invalid_response'); },
    get NO_AUDIO_ID() { return t('common.audio_no_id_response'); },
    get TOO_SHORT() { return t('common.audio_too_short'); },
    get MICROPHONE_DENIED() { return t('common.audio_microphone_denied'); },
    get NOT_SUPPORTED() { return t('common.audio_not_supported'); },
    get SEND_FAILED() { return t('common.audio_send_failed'); }
};

/**
 * Parse JSON response preserving large integer IDs as strings
 * @param {string} responseText
 * @returns {Object}
 */
export function parseAudioResponse(responseText) {
    return JSON.parse(responseText, (key, value) => {
        if ((key === 'id' || key === 'audio_id' || key === 'uid') && typeof value === 'number') {
            return String(value);
        }
        return value;
    });
}

/**
 * Parse server error status into user-friendly message
 * @param {number} status
 * @param {string} [responseText]
 * @returns {string}
 */
export function parseServerError(status, responseText) {
    // Prefer the message the API sent, which is localized for the requesting user
    const message = apiErrorMessage(responseText);
    if (message) {
        return message;
    }

    switch (status) {
        case 401:
        case 403:
            return t('common.audio_auth_required');
        case 413:
            return t('common.audio_upload_failed');
        case 500:
        case 502:
        case 503:
            return t('common.audio_upload_failed');
        default:
            return `${AudioUploadErrors.UPLOAD_FAILED}: ${status}`;
    }
}

/**
 * Get the best supported audio MIME type for MediaRecorder
 * @returns {string}
 */
export function getSupportedMimeType() {
    const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg', 'audio/mp4;codecs=aac', 'audio/mp4'];
    for (const type of types) {
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
            return type;
        }
    }
    // Return empty string to let the browser pick default
    return '';
}

/**
 * Upload an audio blob to the API
 * @param {Object} options
 * @param {Blob} options.blob - Audio blob to upload
 * @param {string} [options.token] - Auth token (will fetch if not provided)
 * @param {AbortController} [options.controller] - Optional abort controller
 * @param {function(number): void} [options.onProgress] - Progress callback (0-100)
 * @returns {Promise<{ id: string, [key: string]: any }>}
 */
export async function uploadAudio({ blob, token, controller, onProgress }) {
    const authToken = token || await getApiToken();
    if (!authToken) {
        throw new Error(AudioUploadErrors.AUTH_REQUIRED);
    }

    const formData = new FormData();
    const ext = blob.type?.includes('mp4') ? 'm4a' : blob.type?.includes('ogg') ? 'ogg' : 'webm';
    formData.append('file', blob, `recording.${ext}`);

    let xhr;
    try {
        xhr = await sendXhr({
            method: 'POST',
            url: AUDIO_UPLOAD_ENDPOINT,
            headers: { 'X-AUTH-TOKEN': authToken },
            body: formData,
            signal: controller?.signal,
            onProgress: onProgress ? (fraction) => onProgress(Math.round(fraction * 100)) : undefined,
        });
    } catch (error) {
        throw new Error(error.kind === 'aborted' ? AudioUploadErrors.UPLOAD_CANCELLED : AudioUploadErrors.NETWORK_ERROR);
    }

    if (xhr.status < 200 || xhr.status >= 300) {
        throw new Error(parseServerError(xhr.status, xhr.responseText));
    }
    let data;
    try {
        data = parseAudioResponse(xhr.responseText);
    } catch {
        throw new Error(AudioUploadErrors.INVALID_RESPONSE);
    }
    // uploaderd wraps audio in {"status":"success","audio":{...}}
    const audio = data?.audio || data;
    if (!audio || !audio.id) {
        console.error('[audio-upload] Missing audio ID in response:', xhr.responseText.substring(0, 200));
        // A body with no audio id can still name the rejection reason, so show that reason.
        throw new Error(apiErrorMessage(xhr.responseText) || AudioUploadErrors.NO_AUDIO_ID);
    }
    return audio;
}
